import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { RoleId } from '../../api/app-types/role.app';
import { Tab } from '../../core/components/tabs/tab';
import { Tabs } from '../../core/components/tabs/tabs';
import { CurrentUserService } from '../../core/services/current-user.service';
import { hasPermission } from '../../shared/permissions/permission.func';
import { SessionManagerService } from '../../shared/storage/session-manager.service';
import { DatabaseAssignmentsList } from './database-assignmnet-list/database-assignments-list';
import { DatabaseInstitutionsList } from './database-institutions-list/database-institutions-list';
import { DatabaseLekList } from './database-lek-list/database-lek-list';
import { DatabaseViewStorage } from './shared/services/database-view-storage.service';

type DatabaseTab = 'institutions' | 'assignments' | 'leks';
interface DatabaseTabItem {
	id: DatabaseTab;
	labelKey: string;
	icon: string;
}

const DATABASE_TABS: readonly DatabaseTab[] = ['institutions', 'assignments', 'leks'];

@Component({
	selector: 'lvr-database',
	templateUrl: './database.html',
	styleUrl: './database.scss',
	imports: [Tab, Tabs, DatabaseAssignmentsList, DatabaseInstitutionsList, DatabaseLekList, TranslocoPipe],
})
export class Database {
	private readonly currentUserService = inject(CurrentUserService);
	private readonly session = inject(SessionManagerService);
	private readonly route = inject(ActivatedRoute);
	private readonly viewStorage = inject(DatabaseViewStorage);
	private readonly queryParams = toSignal(this.route.queryParamMap, {
		initialValue: this.route.snapshot.queryParamMap,
	});
	private readonly lastTab = signal<DatabaseTab | null>(this.viewStorage.getLastTab() ?? null);
	private readonly hasSystemAdminPermission = hasPermission(this.session.permissions, 'System.Admin');
	private readonly user = computed(() => this.currentUserService.currentUser());
	public readonly isAdmin = computed(() => {
		const role = this.user()?.roles?.shortName?.toLowerCase();
		return this.hasSystemAdminPermission() || role === 'admin';
	});
	public readonly isIfdCoordinator = computed(() => {
		const roleId = this.user()?.roles?.id;
		return roleId === RoleId.IFD_COORDINATION;
	});

	public readonly tabs = computed<DatabaseTabItem[]>(() => {
		const tabs: DatabaseTabItem[] = [
			{ id: 'institutions', labelKey: 'database_view.tabs.institutions', icon: 'business' },
			{ id: 'assignments', labelKey: 'database_view.tabs.assignments', icon: 'assignment' },
		];

		if (this.isAdmin() || this.isIfdCoordinator()) {
			tabs.push({
				id: 'leks',
				labelKey: 'database_view.tabs.leks',
				icon: 'school',
			});
		}

		return tabs;
	});

	public constructor() {
		effect(() => {
			const key = this.queryParams().get('list');
			if (!this.isDatabaseTab(key)) {
				return;
			}
			this.lastTab.set(key);
			this.viewStorage.setLastTab(key);
		});
	}

	public readonly activeTab = computed<DatabaseTab>(() => {
		const key = this.queryParams().get('list');
		if (this.isDatabaseTab(key)) {
			return key;
		}

		const lastTab = this.lastTab();
		if (lastTab !== null && this.tabs().some((tab) => tab.id === lastTab)) {
			return lastTab;
		}
		return 'institutions';
	});

	public tabRoute(tab: DatabaseTab) {
		return [`/database?list=${tab}`];
	}

	public isTabActive(tab: DatabaseTab) {
		return this.activeTab() === tab;
	}

	private isDatabaseTab(value: string | null): value is DatabaseTab {
		return value != null && DATABASE_TABS.includes(value as DatabaseTab);
	}
}
